import React, { FC } from 'react';
import { Box, HStack, Text } from '@chakra-ui/react';
import { Tooltip } from '@app/components/Tooltip';
import { AlgebraPoolNitroDetails } from '@app/hooks/nitro/useAlgebraPoolNitroDetails';

import s from './NitroCellRenderer.module.scss';
import { HIDDEN_NITRO_POOLS } from '@app/constants/common';
import { MarketMakerRewards } from '@app/hooks/merkl/useMarketMakerRewards';
import { formatUSD } from '@app/helpers/formatUSD';
import { formatNumber } from '@app/helpers/format';

interface Props {
  data: AlgebraPoolNitroDetails | null;
  nitroRewards: MarketMakerRewards[];
  totalNitroRewardsUSD: number;
}

export const NitroCellRenderer: FC<Props> = ({
  data,
  nitroRewards,
  totalNitroRewardsUSD
}) => {
  if (!data || HIDDEN_NITRO_POOLS.includes(data.id)) {
    return null;
  }

  const grouped = nitroRewards.reduce<Record<string, number>>((r, item) => {
    if (r[item.rewardsToken.symbol]) {
      r[item.rewardsToken.symbol] += +item.dailyRewards;
    } else {
      r[item.rewardsToken.symbol] = +item.dailyRewards;
    }

    return r;
  }, {});

  return (
    <Tooltip
      className={s.tooltip}
      tooltip={
        <Box
          sx={{
            padding: '8px 14px',
            backgroundColor: 'gray.2',
            borderRadius: '10px',
            color: 'nitro',
            fontSize: 14
          }}
        >
          <HStack justifyContent="space-between">
            <Text fontSize="12px" color="nitro">
              TVL:
            </Text>
            <Text color="neutral.100" fontSize="12px">
              {data.tvl}
            </Text>
          </HStack>
          <HStack justifyContent="space-between">
            <Text fontSize="12px" color="nitro">
              Duration:
            </Text>
            <Text color="neutral.100" fontSize="12px">
              {data.duration}
            </Text>
          </HStack>
          <HStack justifyContent="space-between">
            <Text fontSize="12px" color="nitro">
              APR:
            </Text>
            <Text color="neutral.100" fontSize="12px">
              {data.apr}
            </Text>
          </HStack>
          <HStack justifyContent="space-between">
            <Text fontSize="12px" color="nitro">
              Min lock:
            </Text>
            <Text color="neutral.100" fontSize="12px">
              {data.minLock} days
            </Text>
          </HStack>
          <HStack justifyContent="space-between">
            <Text fontSize="12px" color="nitro">
              Pending rewards:
            </Text>
            <Text color="neutral.100" fontSize="12px">
              {formatUSD.format(+data.pendingRewards)}
            </Text>
          </HStack>
          <HStack justifyContent="space-between">
            <Text fontSize="12px" color="warning.100">
              Daily MM rewards:
            </Text>
            <Text color="neutral.100" fontSize="12px" as="span">
              {Object.keys(grouped).map(symbol => {
                const value = grouped[symbol];

                return (
                  <div key={symbol}>
                    {formatNumber(value)} {symbol}
                  </div>
                );
              })}
            </Text>
            <Text color="neutral.100" fontSize="12px">
              ({formatUSD.format(totalNitroRewardsUSD)})
            </Text>
          </HStack>
        </Box>
      }
      placement={'top'}
      zIndex={1000000}
    >
      <Text fontSize="16px" color="nitro">
        {`${formatUSD.format(+data.pendingRewards + totalNitroRewardsUSD)}`}
      </Text>
    </Tooltip>
  );
};

export default NitroCellRenderer;
